// Reactive store for each agent's goal / background tasks / retry (CYBORG-1416). Latest wins: a
// push or a fetch REPLACES the entry, and an empty activity forgets it.
import { EMPTY_ACTIVITY, type AgentActivity } from "./agent-activity.js";

export class AgentActivityStore {
  private byAgent = $state<Record<string, AgentActivity>>({});
  // Per-agent write counter (plain, not reactive) plus an epoch bumped on logout, so a read that started
  // earlier can tell it was overtaken by a push for the same agent.
  private writes: Record<string, number> = {};
  private epoch = 0;

  stamp(agentId: string): string {
    return `${this.epoch}:${this.writes[agentId] ?? 0}`;
  }

  // A fetch result is applied only if nothing wrote that agent since it was requested: a push is newer.
  replaceIfUnchanged(since: string, agentId: string, activity: AgentActivity): boolean {
    if (this.stamp(agentId) !== since) return false;
    this.replace(agentId, activity);
    return true;
  }

  forAgent(agentId: string | undefined): AgentActivity {
    return (agentId && this.byAgent[agentId]) || EMPTY_ACTIVITY;
  }

  replace(agentId: string, activity: AgentActivity): void {
    this.writes[agentId] = (this.writes[agentId] ?? 0) + 1;
    if (
      activity.goal === null &&
      activity.tasks.length === 0 &&
      !activity.processes?.length &&
      activity.retry === null &&
      !activity.rateLimit &&
      !activity.mcp?.length
    ) {
      delete this.byAgent[agentId];
      return;
    }
    this.byAgent[agentId] = activity;
  }

  clearAll(): void {
    this.epoch += 1;
    this.writes = {};
    this.byAgent = {};
  }
}

// The one instance. app.svelte.ts re-exports it and clears it on logout.
export const agentActivityStore = new AgentActivityStore();
